import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { Filter, shapes } from '@unite-us/client-utils';
import { Drawer } from '@unite-us/ui';
import { Spinner } from 'common/spinners';
import { TOP_NAVIGATION_HEIGHT } from 'src/components/Navigation/constants';
import { DynamicTable } from 'common/tables';
import firstLine from 'common/utils/firstLine';
import CaseRowIcon from 'src/components/Dashboard/components/Cases/CaseRowIcon';
import _ from 'lodash';
import { showFacesheetCasesTableDescription } from 'common/utils/FeatureFlags/flags';
import { setFilterSearchPlaceholder, setAsyncSearch } from '../../utils/filter';
import {
  clientNameColumn,
  serviceTypeColumn,
} from '../../utils/columnConfigs/baseColumns';
import FilterBar from '../FilterBar';
import './AllNetworkCasesIndex.scss';

function getColumns(showDescription, sortConfig, onSortColumn) {
  const descColumn = {
    label: 'Description',
    formatData: (data) => firstLine(data.description),
    className: 'truncate',
  };

  const dateOpenedColumn = {
    label: 'Date Opened',
    type: 'date',
    sortColumn: 'opened_date',
    sortBy: sortConfig?.sidx,
    sortDirection: sortConfig?.sord,
    setSort: ({ sortBy, sortDirection }) => onSortColumn(sortBy, sortDirection),
  };

  const lastUpdatedColumn = {
    label: 'Last Updated',
    type: 'date',
    sortColumn: 'updated_at',
    sortBy: sortConfig?.sidx,
    sortDirection: sortConfig?.sord,
    setSort: ({ sortBy, sortDirection }) => onSortColumn(sortBy, sortDirection),
  };

  return _.compact([
    clientNameColumn(sortConfig, onSortColumn),
    {
      label: 'Organization',
      sortColumn: 'provider.name',
    },
    showDescription ? descColumn : null,
    serviceTypeColumn(),
    dateOpenedColumn,
    {
      label: 'Status',
      sortColumn: 'status',
      formatData: (data) => (_.isEmpty(data.outcome) ? 'Open' : 'Closed'),
      type: 'status',
    },
    lastUpdatedColumn,
  ]);
}

export function AllNetworkCasesIndex(props) {
  const {
    data,
    filters,
    initialSort,
    isCaseMgmtProvider,
    isFetching,
    onCellClick,
    onFiltersChange,
    onNextClick,
    onPrevClick,
    paging,
    fetchGroupsUsers,
    onClose,
    onToggle,
    open,
    showDescription,
    sortConfig,
    setSort,
  } = props;
  const secondaryFilters = filters.filter((f) => _.get(f, 'secondary'));

  const handleSortColumn = (sortBy, sortDirection) => {
    if (setSort) {
      setSort(sortBy, sortDirection);
    }
  };

  return (
    <div>
      <FilterBar
        filters={filters}
        onFiltersChange={onFiltersChange}
        paging={paging}
        onNextClick={onNextClick}
        onPrevClick={onPrevClick}
        isFetching={isFetching && !_.isEmpty(data)}
        asyncSearch={fetchGroupsUsers}
        onMoreFiltersClick={onToggle}
        showSecondaryFilters={!_.isEmpty(secondaryFilters)}
      />
      <div className="dashboard-inner-content">
        {
          (isFetching && _.isEmpty(data)) ?
            <Spinner /> : (
              <DynamicTable
                id="all-external-cases-table"
                dataSet={data}
                onCellClick={onCellClick}
                initialSort={initialSort}
                columnLabels={getColumns(showDescription, sortConfig, handleSortColumn)}
                rowIcon={(serviceCase) => (
                  <CaseRowIcon
                    isCaseMgmtProvider={isCaseMgmtProvider}
                    serviceCase={serviceCase}
                  />
                )}
                emptyMessage="There are no cases."
              />
            )
        }
      </div>
      <Drawer
        className="all-network-cases__drawer"
        open={open}
        secondary
        onClose={onClose}
        topOffset={TOP_NAVIGATION_HEIGHT}
      >
        <div className="all-network-cases__drawer-contents">
          <div className="all-network-cases__drawer-header">
            <h2 className="all-network-cases__drawer-header-label">
              More Filter(s)
            </h2>
          </div>
          <div className="all-network-cases__drawer-body">
            {
                _.map(_.orderBy(secondaryFilters, ['name'], ['desc']), (f) => {
                  const {
                    asyncSearch: filterAsyncSearch,
                    searchPlaceholder,
                  } = f;

                  if (!f.secondary) {
                    return null;
                  }

                  return (
                    <Filter
                      hideSelectAll={f.hideSelectAll || false}
                      key={f.key}
                      name={f.name}
                      pluralName={f.pluralName}
                      options={f.options}
                      onFiltersChange={(activeFilters) => onFiltersChange(f.key, activeFilters)}
                      searchPlaceholder={searchPlaceholder || setFilterSearchPlaceholder(f)}
                      asyncSearch={filterAsyncSearch !== undefined ?
                        setAsyncSearch(f, filterAsyncSearch) :
                        setAsyncSearch(f, fetchGroupsUsers)}
                      uniqIdPrefix={f.key}
                    />
                  );
                })
              }
          </div>
        </div>
      </Drawer>
    </div>
  );
}

AllNetworkCasesIndex.propTypes = {
  data: PropTypes.array.isRequired,
  filters: PropTypes.array,
  initialSort: PropTypes.object,
  isCaseMgmtProvider: PropTypes.bool,
  isFetching: PropTypes.bool.isRequired,
  onCellClick: PropTypes.func.isRequired,
  onFiltersChange: PropTypes.func,
  onNextClick: PropTypes.func.isRequired,
  onPrevClick: PropTypes.func.isRequired,
  paging: shapes.paging,
  fetchGroupsUsers: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  showDescription: PropTypes.bool,
  sortConfig: PropTypes.object,
  setSort: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    showDescription: showFacesheetCasesTableDescription(state),
  };
}

export default connect(mapStateToProps)(AllNetworkCasesIndex);
